import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { FileCheck, CheckCircle, Clock, XCircle, Upload, X, Camera, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { CardGuardUpload } from '../components/CardGuardUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function KYCPage() {
  const { t } = useLanguage();
  const [kycStatus, setKycStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [documentPhotos, setDocumentPhotos] = useState({ front: null, back: null, angled: null });
  const [formData, setFormData] = useState({
    document_type: 'residence_card',
    document_number: '',
    full_name: '',
    date_of_birth: '',
    address: '',
    occupation: '',
    purpose_of_transfer: '',
    residence_status: '',
    residence_card_number: '',
    residence_period: '',
    document_files: []
  });

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      const response = await api.getKYCStatus();
      setKycStatus(response.data);
      if (!response.data || response.data.status === 'not_submitted') {
        setShowForm(true);
      }
    } catch (error) {
      console.error('Failed to fetch KYC status:', error);
      setShowForm(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/kyc/history`);
      setHistory(response.data || []);
    } catch (e) { console.error(e); }
  };

  const handleToggleHistory = () => {
    if (!showHistory && history.length === 0) fetchHistory();
    setShowHistory(!showHistory);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allFiles = Object.values(documentPhotos).filter(Boolean);
    setSubmitting(true);
    try {
      await api.submitKYC({ ...formData, document_files: allFiles });
      toast.success(t('kyc_submitted_toast'));
      setShowForm(false);
      fetchKYCStatus();
      if (showHistory) fetchHistory();
      setDocumentPhotos({ front: null, back: null, angled: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('kyc_submit_fail'));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoUpload = async (side, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(prev => ({ ...prev, [side]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API_URL}/api/kyc/upload`, fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setDocumentPhotos(prev => ({
        ...prev,
        [side]: {
          file_id: response.data.file_id,
          filename: response.data.filename,
          content_type: response.data.content_type,
          file_url: response.data.file_url,
          side: side
        }
      }));
      toast.success(t('kyc_files_uploaded', { count: 1 }));
    } catch (error) {
      toast.error(t('kyc_upload_fail'));
    } finally {
      setUploading(prev => ({ ...prev, [side]: false }));
    }
  };

  const removePhoto = (side) => {
    setDocumentPhotos(prev => ({ ...prev, [side]: null }));
  };

  const photoSides = [
    { key: 'front', label: t('kyc_photo_front') },
    { key: 'back', label: t('kyc_photo_back') },
    { key: 'angled', label: t('kyc_photo_angled') }
  ];

  const getStatusDisplay = () => {
    if (!kycStatus || kycStatus.status === 'not_submitted') return null;

    const statusConfig = {
      pending: { icon: Clock, title: t('kyc_status_pending_title'), description: t('kyc_status_pending_desc'), color: 'text-blue-600', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
      submitted: { icon: Clock, title: t('kyc_status_pending_title'), description: t('kyc_status_pending_desc'), color: 'text-blue-600', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
      approved: { icon: CheckCircle, title: t('kyc_status_approved_title'), description: t('kyc_status_approved_full'), color: 'text-emerald-600', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
      rejected: { icon: XCircle, title: t('kyc_status_rejected_title'), description: t('kyc_status_rejected_full'), color: 'text-red-600', bg: 'bg-red-500/10', border: 'border-red-500/20' }
    };

    const config = statusConfig[kycStatus.status];
    if (!config) return null;
    const Icon = config.icon;

    return (
      <div className={`${config.bg} border ${config.border} rounded-2xl p-6 mb-8`} data-testid="kyc-status-card">
        <div className="flex items-start gap-4">
          <Icon className={`h-8 w-8 ${config.color} flex-shrink-0`} />
          <div className="flex-1">
            <h3 className="font-heading text-xl font-semibold text-primary mb-2">{config.title}</h3>
            <p className="text-muted-foreground mb-4">{config.description}</p>
            {kycStatus.reviewer_notes && (
              <div className="bg-card/50 rounded-lg p-4">
                <div className="text-sm font-medium text-primary mb-1">{t('kyc_reviewer_notes')}</div>
                <div className="text-sm text-muted-foreground">{kycStatus.reviewer_notes}</div>
              </div>
            )}
            {/* Residence info */}
            {(kycStatus.residence_status || kycStatus.residence_card_number || kycStatus.residence_period) && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {kycStatus.residence_status && (
                  <div className="bg-card/50 rounded-lg p-3">
                    <div className="text-xs text-muted-foreground">{t('kyc_residence_status')}</div>
                    <div className="text-sm font-medium">{kycStatus.residence_status}</div>
                  </div>
                )}
                {kycStatus.residence_card_number && (
                  <div className="bg-card/50 rounded-lg p-3">
                    <div className="text-xs text-muted-foreground">{t('kyc_residence_card_number')}</div>
                    <div className="text-sm font-medium font-mono">{kycStatus.residence_card_number}</div>
                  </div>
                )}
                {kycStatus.residence_period && (
                  <div className="bg-card/50 rounded-lg p-3">
                    <div className="text-xs text-muted-foreground">{t('kyc_residence_period')}</div>
                    <div className="text-sm font-medium">{kycStatus.residence_period}</div>
                  </div>
                )}
              </div>
            )}
            {/* Submitted files */}
            {kycStatus.document_files && kycStatus.document_files.length > 0 && (
              <div className="mt-4">
                <div className="text-sm font-medium text-primary mb-2">{t('kyc_submitted_docs')}</div>
                <div className="grid grid-cols-3 gap-3">
                  {kycStatus.document_files.map((file, idx) => (
                    <div key={idx} className="text-center" data-testid={`kyc-status-file-${idx}`}>
                      {file.content_type?.startsWith('image/') ? (
                        <img src={`${API_URL}${file.file_url}`} alt={file.side || file.filename} className="h-24 w-full rounded-lg object-cover border border-border" />
                      ) : (
                        <a href={`${API_URL}${file.file_url}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 text-sm text-accent hover:underline h-24 border border-border rounded-lg">
                          {file.filename || 'PDF'}
                        </a>
                      )}
                      <div className="text-xs text-muted-foreground mt-1">
                        {file.side === 'front' && t('kyc_photo_front')}
                        {file.side === 'back' && t('kyc_photo_back')}
                        {file.side === 'angled' && t('kyc_photo_angled')}
                        {!file.side && (file.filename || `${idx + 1}`)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4 text-xs text-muted-foreground">
              {t('kyc_submitted_at')}: {new Date(kycStatus.submitted_at).toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl" data-testid="kyc-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">{t('kyc_title')}</h1>
          <p className="text-muted-foreground">{t('kyc_page_subtitle')}</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
        ) : (
          <>
            {getStatusDisplay()}
            {kycStatus && kycStatus.status !== 'not_submitted' && !showForm && (
              <div className="flex gap-3 mb-6">
                <Button onClick={() => setShowForm(true)} className="rounded-full" data-testid="add-new-kyc-btn">
                  <Plus className="h-4 w-4 mr-2" />{t('doc_add_new')}
                </Button>
              </div>
            )}
            {showForm && (
              <div className="bg-card border border-border/50 rounded-2xl p-8">
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <h2 className="font-heading text-xl font-semibold text-primary mb-2">{t('kyc_form_title')}</h2>
                    <p className="text-sm text-muted-foreground">{t('kyc_form_desc')}</p>
                  </div>
                  {kycStatus && kycStatus.status !== 'not_submitted' && (
                    <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-full" data-testid="cancel-new-kyc-btn">
                      {t('cancel')}
                    </Button>
                  )}
                </div>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="kyc-form">
                  <div className="space-y-2">
                    <Label htmlFor="document_type">{t('kyc_doc_type_label')}</Label>
                    <Select value={formData.document_type} onValueChange={(value) => setFormData({...formData, document_type: value})}>
                      <SelectTrigger data-testid="document-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="passport">{t('kyc_doc_type_passport')}</SelectItem>
                        <SelectItem value="residence_card">{t('kyc_doc_type_residence')}</SelectItem>
                        <SelectItem value="drivers_license">{t('kyc_doc_type_license')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="document_number">{t('kyc_doc_number')}</Label>
                    <Input id="document_number" value={formData.document_number} onChange={(e) => setFormData({...formData, document_number: e.target.value})} required data-testid="document-number-input" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="full_name">{t('kyc_full_name_label')}</Label>
                    <Input id="full_name" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} required data-testid="fullname-input" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">{t('kyc_dob')}</Label>
                    <Input id="date_of_birth" type="date" value={formData.date_of_birth} onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})} required data-testid="dob-input" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address">{t('kyc_address')}</Label>
                    <Textarea id="address" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} rows={3} required data-testid="address-input" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="occupation">{t('kyc_occupation')}</Label>
                    <Input id="occupation" value={formData.occupation} onChange={(e) => setFormData({...formData, occupation: e.target.value})} required data-testid="occupation-input" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="purpose_of_transfer">{t('kyc_purpose')}</Label>
                    <Textarea id="purpose_of_transfer" value={formData.purpose_of_transfer} onChange={(e) => setFormData({...formData, purpose_of_transfer: e.target.value})} placeholder={t('kyc_purpose_placeholder')} rows={3} required data-testid="purpose-input" />
                  </div>

                  {/* Residence fields */}
                  <div className="border-t border-border pt-6">
                    <h3 className="font-heading text-lg font-semibold text-primary mb-4">{t('kyc_residence_section')}</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="residence_status">{t('kyc_residence_status')}</Label>
                        <Select value={formData.residence_status} onValueChange={(value) => setFormData({...formData, residence_status: value})}>
                          <SelectTrigger data-testid="residence-status-select"><SelectValue placeholder={t('kyc_residence_status_placeholder')} /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="permanent_resident">{t('kyc_residence_permanent')}</SelectItem>
                            <SelectItem value="long_term_resident">{t('kyc_residence_long_term')}</SelectItem>
                            <SelectItem value="spouse_of_japanese">{t('kyc_residence_spouse')}</SelectItem>
                            <SelectItem value="work_visa">{t('kyc_residence_work')}</SelectItem>
                            <SelectItem value="student_visa">{t('kyc_residence_student')}</SelectItem>
                            <SelectItem value="technical_intern">{t('kyc_residence_technical')}</SelectItem>
                            <SelectItem value="specified_skilled">{t('kyc_residence_specified')}</SelectItem>
                            <SelectItem value="other">{t('kyc_residence_other')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="residence_card_number">{t('kyc_residence_card_number')}</Label>
                        <Input id="residence_card_number" value={formData.residence_card_number} onChange={(e) => setFormData({...formData, residence_card_number: e.target.value})} placeholder="AB12345678CD" data-testid="residence-card-number-input" />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="residence_period">{t('kyc_residence_period')}</Label>
                        <Input id="residence_period" type="date" value={formData.residence_period} onChange={(e) => setFormData({...formData, residence_period: e.target.value})} data-testid="residence-period-input" />
                      </div>
                    </div>
                  </div>

                  {/* Document Photo Upload - 3 sides */}
                  <div className="border-t border-border pt-6">
                    <h3 className="font-heading text-lg font-semibold text-primary mb-2">{t('kyc_documents')}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{t('kyc_photo_desc')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {photoSides.map(({ key, label }) => (
                        <div key={key} className="space-y-2">
                          <Label className="text-sm">{label}</Label>
                          <CardGuardUpload
                            label={label}
                            uploading={uploading[key]}
                            previewUrl={documentPhotos[key] ? `${API_URL}${documentPhotos[key].file_url}` : null}
                            onUpload={(file) => {
                              const e = { target: { files: [file] } };
                              handlePhotoUpload(key, e);
                            }}
                            onRemove={() => removePhoto(key)}
                            testId={`photo-upload-${key}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button type="submit" size="lg" className="w-full rounded-full h-12" disabled={submitting} data-testid="submit-kyc-button">
                    {submitting ? t('kyc_submitting') : t('kyc_submit_button')}
                  </Button>
                </form>
              </div>
            )}
            {/* History section */}
            {kycStatus && kycStatus.status !== 'not_submitted' && (
              <div className="mt-6">
                <button
                  onClick={handleToggleHistory}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-kyc-history"
                >
                  {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {t('doc_history')}
                </button>
                {showHistory && (
                  <div className="mt-3 space-y-3" data-testid="kyc-history">
                    {history.length === 0 ? (
                      <p className="text-sm text-muted-foreground">{t('loading')}</p>
                    ) : history.map((item, idx) => {
                      const statusColors = {
                        pending: 'text-yellow-600 bg-yellow-50 border-yellow-200',
                        submitted: 'text-blue-600 bg-blue-50 border-blue-200',
                        approved: 'text-emerald-600 bg-emerald-50 border-emerald-200',
                        rejected: 'text-red-600 bg-red-50 border-red-200',
                      };
                      const statusLabels = {
                        pending: t('kyc_status_pending_title'),
                        submitted: t('kyc_status_pending_title'),
                        approved: t('kyc_status_approved_title'),
                        rejected: t('kyc_status_rejected_title'),
                      };
                      return (
                        <div key={item.id} className="bg-card border border-border/50 rounded-xl p-4 flex items-center justify-between" data-testid={`kyc-history-item-${idx}`}>
                          <div>
                            <div className="text-sm font-medium">{item.document_type} - {item.full_name}</div>
                            <div className="text-xs text-muted-foreground">{new Date(item.submitted_at).toLocaleDateString()}</div>
                          </div>
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${statusColors[item.status] || statusColors.pending}`}>
                            {statusLabels[item.status] || item.status}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
